import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { TID } from "../lib/testIds";

const HERO_LINES = ["Events", "Reimagined."];

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.14, delayChildren: 0.15 },
  },
};

const line = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 1.15, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* parallax background photograph */}
      <motion.div
        style={{ y: yImg, scale: scaleImg }}
        className="absolute inset-0 -z-10 will-change-transform"
      >
        <img
          src="https://images.unsplash.com/photo-1618123664298-f3a0281e7e44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxpbW1lcnNpdmUlMjBsaWdodCUyMGFydCUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3ODM2NTk5NjN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-between">
        <div>
          {/* eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-white/40" />
            <span className="font-mono-em text-[0.7rem] tracking-[0.28em] uppercase text-white/70">
              Est. Kochi · IN — Independent Studio
            </span>
          </motion.div>

          {/* kinetic masked headline — calmer */}
          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="mt-10 md:mt-14 font-display font-extrabold text-white leading-[0.92] tracking-tighter"
            style={{ fontSize: "clamp(3rem, 8vw, 8.4rem)" }}
          >
            {HERO_LINES.map((l, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  variants={line}
                  className="block will-change-transform"
                >
                  {i === 1 ? <span className="gradient-text">{l}</span> : l}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <div className="mt-12 grid grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 md:col-span-6 lg:col-span-5 text-white/70 text-base md:text-lg leading-relaxed"
            >
              We design, direct and produce festivals, launches and brand
              experiences that behave like culture — an independent studio
              based in Kochi, working across India.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 md:col-span-6 lg:col-span-4 lg:col-start-9 flex flex-wrap gap-3"
            >
              <a
                href="#contact"
                data-testid={TID.heroCta}
                className="em-btn em-btn-primary"
              >
                Start a Project <ArrowUpRight size={16} />
              </a>
              <a
                href="#work"
                data-testid={TID.heroSecondaryCta}
                className="em-btn em-btn-ghost border-white/20"
              >
                See Our Work
              </a>
            </motion.div>
          </div>
        </div>

        {/* footer strip — compact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-16 md:mt-24 flex items-end justify-between border-t border-white/10 pt-6"
        >
          <div className="flex items-center gap-3 text-white/50 font-mono-em text-[0.7rem] tracking-[0.24em] uppercase">
            <ArrowDown size={14} className="animate-bounce" /> Scroll
          </div>
          <div className="font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/50 text-right">
            <div>12°58′N 76°16′E</div>
            <div className="mt-1 hidden md:block">Live · Sponsor · Launch · Film</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
